// 카리페이 콘솔 라우트. 카리아카 page_route.tsx 의 기관장 "매출 관리" 묶음만 옮겼다.
// CorpMenu 배열 순서 = 사이드바 순서. containPath 는 하위 화면에서도 메뉴를 활성으로 두는 경로다.
import React, { lazy, Suspense } from "react";
import { IconClass, IconClassFilled } from "@/components/icons/class";
import { IconWallet, IconWalletFilled } from "@/components/icons/wallet";
import IconSend from "@/components/icons/send";
import SettingsIcon from "@/components/icons/settings";
import colors from "@/design/colors";

// 로그인 직후·로고 클릭 시 가는 첫 화면
export const HOME_PATH = "/payment/sales-report";

// 인증 ////////////////////////////////////////////////////////////////////
const Login = lazy(() => import("@/pages/auth/Login"));
const Signup = lazy(() => import("@/pages/auth/Signup"));
const FindEmail = lazy(() => import("@/pages/auth/FindEmail"));
const ResetPassword = lazy(() => import("@/pages/auth/ResetPassword"));
const Onboarding = lazy(() => import("@/pages/auth/Onboarding"));
const Legal = lazy(() => import("@/pages/Legal"));

// 매출·청구·수납 ///////////////////////////////////////////////////////////
const SalesReport = lazy(() => import("@/pages/corp/payment_management/sales_report/SalesReport"));
const BillHistory = lazy(() => import("@/pages/corp/payment_management/bill_history/BillHistory"));
const BillHistoryEdit = lazy(() => import("@/pages/corp/payment_management/bill_history/bill_edit/BillHistoryEdit"));
const BillHistoryResend = lazy(() => import("@/pages/corp/payment_management/bill_history/bill_resend/BillHistoryResend"));
const BillHistoryDelete = lazy(() => import("@/pages/corp/payment_management/bill_history/bill_delete/BillHistoryDelete"));
const BillHistoryDownload = lazy(() => import("@/pages/corp/payment_management/bill_history/bill_download/BillHistoryDownload"));
const BillOnSite = lazy(() => import("@/pages/corp/payment_management/bill_history/bill_onSite/BillOnSite"));
const BillOnSiteEdit = lazy(() => import("@/pages/corp/payment_management/bill_history/bill_onSite/BillOnSiteEdit"));
const BillSend = lazy(() => import("@/pages/corp/payment_management/bill_send/BillSend"));
const BillIndividualSend = lazy(() => import("@/pages/corp/payment_management/bill_send/BillIndividualSend"));
const BillBulkExcel = lazy(() => import("@/pages/corp/payment_management/bill_send/BillBulkExcel"));
const BillSchedule = lazy(() => import("@/pages/corp/payment_management/bill_schedule_history/BillSchedulePage"));
const BillScheduleSendNow = lazy(() => import("@/pages/corp/payment_management/bill_schedule_history/schedule_control/BillScheduleSendNowPage"));
const BillScheduleCancel = lazy(() => import("@/pages/corp/payment_management/bill_schedule_history/schedule_control/BillScheduleCancelPage"));
const BillScheduleDetail = lazy(() => import("@/pages/corp/payment_management/bill_schedule_history/schedule_detail/BillScheduleDetail"));
const BillScheduleEdit = lazy(() => import("@/pages/corp/payment_management/bill_schedule_history/schedule_edit/BillScheduleRecipientPage"));
const RecipientSendNow = lazy(() => import("@/pages/corp/payment_management/bill_schedule_history/recipient_control/RecipientSendNowPage"));
const RecipientCancel = lazy(() => import("@/pages/corp/payment_management/bill_schedule_history/recipient_control/RecipientCancelPage"));
const RecipientCancelIndividual = lazy(() => import("@/pages/corp/payment_management/bill_schedule_history/recipient_control/RecipientCancelIndividualPage"));
const BillTemplatePage = lazy(() => import("@/pages/corp/payment_management/bill_template/BillTemplatePage"));
const BillCreateTemplate = lazy(() => import("@/pages/corp/payment_management/bill_template/bill_setting/BillCreateTemplate"));
const BillCreateTemplateIndividualPage = lazy(() => import("@/pages/corp/payment_management/bill_template/bill_setting/BillCreateTemplateIndividualPage"));
const BillEditTemplateIndividualPage = lazy(() => import("@/pages/corp/payment_management/bill_template/bill_setting/BillEditTemplateIndividualPage"));
const BillDetailTemplate = lazy(() => import("@/pages/corp/payment_management/bill_template/bill_setting/BillDetailTemplate"));
const BillEditTemplate = lazy(() => import("@/pages/corp/payment_management/bill_template/bill_setting/BillEditTemplate"));
const BillContactPage = lazy(() => import("@/pages/corp/payment_management/bill_contact/BillContactPage"));
const PaymentHistory = lazy(() => import("@/pages/corp/payment_management/payment_history/PaymentHistory"));
const PaymentHistoryDownload = lazy(() => import("@/pages/corp/payment_management/payment_history/payment_download/PaymentHistoryDownload"));
const PaymentWrite = lazy(() => import("@/pages/corp/payment_management/payment_history/payment_write/PaymentWrite"));
const PaymentEditPage = lazy(() => import("@/pages/corp/payment_management/payment_history/payment_edit/PaymentEditPage"));
const PaymentOnSiteEditPage = lazy(() => import("@/pages/corp/payment_management/payment_history/payment_edit/PaymentOnSiteEditPage"));

// 포인트·설정 //////////////////////////////////////////////////////////////
const PointPage = lazy(() => import("@/pages/corp/point/Point"));
const BusinessInfoPage = lazy(() => import("@/pages/settings/BusinessInfoPage"));
const AccountPage = lazy(() => import("@/pages/settings/AccountPage"));
const BusinessRegistrationInfo = lazy(() => import("@/pages/corp/businessRegistration/BusinessRegistration_info"));
const BusinessRegistration = lazy(() => import("@/pages/corp/businessRegistration/add/BusinessRegistration"));
const BusinessRegistrationEdit = lazy(() => import("@/pages/corp/businessRegistration/edit/BusinessRegistration"));
const SenderRegistration = lazy(() => import("@/pages/corp/sender_registration/SenderRegistration"));

const withSuspense = (Component: React.ComponentType) => () => (
  <Suspense fallback={null}>
    <Component />
  </Suspense>
);

export interface ISubMenu {
  path: string;
  title: string;
  component: () => React.ReactNode;
  containPath?: string[];
}

export interface IMenu {
  path: string;
  title: string;
  iconActive: React.ReactNode;
  iconInactive: React.ReactNode;
  children: (ISubMenu & { containPath: string[] })[];
}

// 사이드바 메뉴(로그인 + 사업장 보유 시)
export const CorpMenu: IMenu[] = [
  {
    path: "/payment/sales-report",
    title: "홈",
    iconActive: <IconClassFilled width="20px" height="20px" color={colors.blue600} />,
    iconInactive: <IconClass width="20px" height="20px" color={colors.gray500} />,
    children: [
      {
        path: "/payment/sales-report",
        title: "매출 대시보드",
        component: withSuspense(SalesReport),
        containPath: ["/payment/sales-report"],
      },
    ],
  },
  {
    path: "/payment/bill-history",
    title: "청구",
    iconActive: <IconSend width="20px" height="20px" color={colors.blue600} />,
    iconInactive: <IconSend width="20px" height="20px" color={colors.gray500} />,
    children: [
      {
        path: "/payment/bill-history",
        title: "청구서 발송내역",
        component: withSuspense(BillHistory),
        containPath: [
          "/payment/bill-history",
          "/payment/bill-send",
          "/payment/bill-individual-send",
          "/payment/bill-history/edit",
          "/payment/bill-history/resend",
          "/payment/bill-history/delete",
          "/payment/bill-history/download",
          "/payment/bill-history/on-site",
          "/payment/bill-history/on-site/:id",
          "/payment/bill-history/on-site-edit/:id",
        ],
      },
      {
        path: "/payment/bill-bulk-excel",
        title: "엑셀 일괄 청구",
        component: withSuspense(BillBulkExcel),
        containPath: ["/payment/bill-bulk-excel"],
      },
      {
        path: "/payment/bill-schedule-history",
        title: "청구서 예약내역",
        component: withSuspense(BillSchedule),
        containPath: [
          "/payment/bill-schedule-history",
          "/payment/bill-schedule-history/send-now",
          "/payment/bill-schedule-history/cancel",
          "/payment/bill-schedule-history/detail/:id",
          "/payment/bill-schedule-history/recipient-edit/:id",
          "/payment/bill-schedule-history/recipient-send-now",
          "/payment/bill-schedule-history/recipient-cancel",
          "/payment/bill-schedule-history/recipient-cancel-individual/:id",
        ],
      },
      {
        path: "/payment/bill-template",
        title: "청구 템플릿",
        component: withSuspense(BillTemplatePage),
        containPath: [
          "/payment/bill-template",
          "/payment/bill-template/create-template",
          "/payment/bill-template/edit-template/:id",
          "/payment/bill-template/detail-template/:id",
          "/payment/bill-template/create-template-individual",
          "/payment/bill-template/edit-template-individual/:id",
        ],
      },
      {
        path: "/payment/bill-contact",
        title: "청구 연락처",
        component: withSuspense(BillContactPage),
        containPath: ["/payment/bill-contact"],
      },
    ],
  },
  {
    path: "/payment/payment-history",
    title: "수납",
    iconActive: <IconWalletFilled width="20px" height="20px" color={colors.blue600} />,
    iconInactive: <IconWallet width="20px" height="20px" color={colors.gray500} />,
    children: [
      {
        path: "/payment/payment-history",
        title: "수납 내역",
        component: withSuspense(PaymentHistory),
        containPath: [
          "/payment/payment-history",
          "/payment/payment-history/download",
          "/payment/payment-history/write",
          "/payment/payment-history/edit/:id",
          "/payment/payment-history/onsite-edit/:id",
        ],
      },
    ],
  },
  {
    path: "/settings/business",
    title: "설정",
    iconActive: <SettingsIcon width="20px" height="20px" color={colors.blue600} />,
    iconInactive: <SettingsIcon width="20px" height="20px" color={colors.gray500} />,
    children: [
      {
        path: "/settings/business",
        title: "사업장 정보",
        component: withSuspense(BusinessInfoPage),
        containPath: [
          "/settings/business",
          "/business-registration",
          "/business-registration/info",
          "/business-registration/edit",
        ],
      },
      {
        path: "/corp/sender-registration",
        title: "발신번호 등록",
        component: withSuspense(SenderRegistration),
        containPath: ["/corp/sender-registration"],
      },
      {
        path: "/settings/account",
        title: "계정",
        component: withSuspense(AccountPage),
        containPath: ["/settings/account"],
      },
    ],
  },
];

// 사이드바에 없는 하위 화면(로그인 + 사업장 보유 시)
export const CorpExtra: ISubMenu[] = [
  { path: "/payment/bill-send", title: "청구서 발송", component: withSuspense(BillSend) },
  { path: "/payment/bill-individual-send", title: "청구서 발송", component: withSuspense(BillIndividualSend) },
  { path: "/payment/bill-schedule-history/send-now", title: "청구서 바로 발송", component: withSuspense(BillScheduleSendNow) },
  { path: "/payment/bill-schedule-history/cancel", title: "청구서 예약 취소", component: withSuspense(BillScheduleCancel) },
  { path: "/payment/bill-schedule-history/detail/:id", title: "예약 청구서", component: withSuspense(BillScheduleDetail) },
  { path: "/payment/bill-schedule-history/recipient-edit/:id", title: "예약 청구서 수정", component: withSuspense(BillScheduleEdit) },
  { path: "/payment/bill-schedule-history/recipient-send-now", title: "예약 청구서 바로 발송", component: withSuspense(RecipientSendNow) },
  { path: "/payment/bill-schedule-history/recipient-cancel", title: "예약 청구서 예약 취소", component: withSuspense(RecipientCancel) },
  { path: "/payment/bill-schedule-history/recipient-cancel-individual/:id", title: "예약 청구서 개별 예약 취소", component: withSuspense(RecipientCancelIndividual) },
  { path: "/payment/bill-template/create-template", title: "청구서 템플릿 생성", component: withSuspense(BillCreateTemplate) },
  { path: "/payment/bill-template/create-template-individual", title: "청구서 템플릿 생성", component: withSuspense(BillCreateTemplateIndividualPage) },
  { path: "/payment/bill-template/edit-template-individual/:id", title: "청구서 템플릿 수정", component: withSuspense(BillEditTemplateIndividualPage) },
  { path: "/payment/bill-template/detail-template/:id", title: "청구서 템플릿 상세", component: withSuspense(BillDetailTemplate) },
  { path: "/payment/bill-template/edit-template/:id", title: "청구서 템플릿 수정", component: withSuspense(BillEditTemplate) },
  { path: "/payment/bill-history/edit", title: "청구서 내역 편집", component: withSuspense(BillHistoryEdit) },
  { path: "/payment/bill-history/resend", title: "청구서 재발송", component: withSuspense(BillHistoryResend) },
  { path: "/payment/bill-history/delete", title: "청구서 삭제", component: withSuspense(BillHistoryDelete) },
  { path: "/payment/bill-history/download", title: "수납 영수증 다운로드", component: withSuspense(BillHistoryDownload) },
  { path: "/payment/bill-history/on-site", title: "현장 수납", component: withSuspense(BillOnSite) },
  { path: "/payment/bill-history/on-site/:id", title: "현장 수납", component: withSuspense(BillOnSite) },
  { path: "/payment/bill-history/on-site-edit/:id", title: "현장 수납 정정", component: withSuspense(BillOnSiteEdit) },
  { path: "/payment/payment-history/download", title: "수납 영수증 다운로드", component: withSuspense(PaymentHistoryDownload) },
  { path: "/payment/payment-history/write", title: "수납 내역 작성", component: withSuspense(PaymentWrite) },
  { path: "/payment/payment-history/edit/:id", title: "수납 내역 수정", component: withSuspense(PaymentEditPage) },
  { path: "/payment/payment-history/onsite-edit/:id", title: "현장수납 수납 정보 정정", component: withSuspense(PaymentOnSiteEditPage) },
  { path: "/point", title: "포인트", component: withSuspense(PointPage) },
  { path: "/business-registration/info", title: "사업장 정보 등록 안내", component: withSuspense(BusinessRegistrationInfo) },
  { path: "/business-registration", title: "사업장 정보 등록", component: withSuspense(BusinessRegistration) },
  { path: "/business-registration/edit", title: "사업장 정보 수정", component: withSuspense(BusinessRegistrationEdit) },
];

// 로그인 전 화면(로그인 상태면 홈으로 보낸다)
export const AuthRoutes: ISubMenu[] = [
  { path: "/login", title: "로그인", component: withSuspense(Login) },
  { path: "/signup", title: "가맹점 가입", component: withSuspense(Signup) },
  { path: "/find-email", title: "이메일 찾기", component: withSuspense(FindEmail) },
  { path: "/reset-password", title: "비밀번호 재설정", component: withSuspense(ResetPassword) },
];

// 로그인했지만 사업장이 없는 계정이 거치는 화면
export const OnboardingRoute: ISubMenu = {
  path: "/onboarding",
  title: "사업장 등록",
  component: withSuspense(Onboarding),
};

// 로그인 여부와 무관하게 열리는 화면
export const PublicRoutes: ISubMenu[] = [
  { path: "/legal/:doc", title: "약관", component: withSuspense(Legal) },
];
